import { useState } from "react"
import type { Registry } from "@/api.js"
import { type ConfigChange, ruleScope } from "./diff.js"
import { assignPriorities, type EditorQuestion, type EditorRule } from "./model.js"
import { RuleEditor } from "./RuleEditor.js"
import { RuleList } from "./RuleList.js"

/**
 * One question's rule set in one country: the list of rules beside the editor
 * for the one that is picked.
 *
 * **A list to find a rule in, and one editor with room, instead of a grid
 * with every control on every row.** The grid was right about one thing — a
 * screen somebody returns to for one tweak must not make them hunt — and got
 * the rest wrong once there were fourteen rules: fourteen rows of the same
 * selects and sliders are a texture, not a list, and the reading matter and
 * the parameters had to be crammed under a row when opened. Picking a rule is
 * one click; every setting of that rule is then in one place, explained.
 *
 * The first rule is picked on arrival, so the editor is never an empty box, and
 * the list marks the rules this draft has edited so they can be found again
 * before publishing.
 */
export function RulesPane({
  country,
  set,
  registry,
  changes,
  onChange,
  onReorder,
}: {
  country: string
  set: EditorQuestion
  registry: Registry
  /** What the draft would publish, so the list can mark the rules it touches. */
  changes: readonly ConfigChange[]
  onChange: (next: EditorRule) => void
  onReorder: (order: readonly EditorRule[]) => void
}) {
  const [picked, setPicked] = useState<string | null>(null)
  const [filter, setFilter] = useState("")

  const rules = set.rules
  const selected = rules.find((rule) => rule.ruleId === picked) ?? rules[0] ?? null

  const running = rules.filter((rule) => rule.enabled)
  const priorities = assignPriorities(running, registry)
  const priorityOf = (rule: EditorRule): number | null => {
    const index = running.indexOf(rule)
    return index === -1 ? null : (priorities[index] ?? null)
  }

  const changed = new Set(
    rules
      .filter((rule) =>
        changes.some((change) => change.scope === ruleScope(country, set.title, rule.title)),
      )
      .map((rule) => rule.ruleId),
  )

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(19rem,24rem)_minmax(0,1fr)]">
      <RuleList
        country={country}
        rules={rules}
        registry={registry}
        threshold={set.blockThreshold}
        filter={filter}
        onFilter={setFilter}
        selected={selected?.ruleId ?? null}
        onSelect={setPicked}
        onReorder={onReorder}
        changed={changed}
      />
      {selected !== null && (
        <RuleEditor
          key={selected.ruleId}
          rule={selected}
          registry={registry}
          threshold={set.blockThreshold}
          priority={priorityOf(selected)}
          onChange={onChange}
        />
      )}
    </div>
  )
}
